import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-status-badge',
  standalone: true,
  imports: [CommonModule],
  template: `
    <span class="status-badge" [ngClass]="statusClass">
      <i *ngIf="showIcon" [class]="statusIcon"></i>
      {{ displayLabel }}
    </span>
  `,
  styles: [`
    .status-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 4px 10px;
      border-radius: 9999px;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.5px;
      text-transform: uppercase;
    }
    /* Status */
    .status-active   { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; }
    .status-inactive { background: #f8fafc; color: #64748b; border: 1px solid #e2e8f0; }
    .status-pending  { background: #fff7ed; color: #ea580c; border: 1px solid #ffedd5; }
    .status-approved { background: #f0fdf4; color: #16a34a; border: 1px solid #dcfce7; }
    .status-rejected { background: #fef2f2; color: #dc2626; border: 1px solid #fee2e2; }
    /* Roles */
    .role-admin    { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
    .role-minister { background: #fffbeb; color: #b45309; border: 1px solid #fde68a; }
    .role-osd      { background: #faf5ff; color: #7e22ce; border: 1px solid #e9d5ff; }
    .role-operator { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
    .role-approver { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }

    i { font-size: 10px; }
  `]
})
export class StatusBadgeComponent {
  @Input() status: any = 'pending';
  @Input() showIcon: boolean = true;

  private get normalized(): string {
    if (typeof this.status === 'boolean') return this.status ? 'active' : 'inactive';
    return String(this.status ?? '').toLowerCase();
  }

  get displayLabel(): string {
    const n = this.normalized;
    const labels: Record<string, string> = {
      active: 'Active', inactive: 'Inactive', pending: 'Pending',
      approved: 'Approved', rejected: 'Rejected',
      admin: 'Admin', osd: 'OSD', minister: 'Minister',
      operator: 'Operator', approver: 'Approver'
    };
    return labels[n] ?? n.toUpperCase();
  }

  get statusClass(): string {
    switch (this.normalized) {
      case 'active': case 'approved': case 'true':  return 'status-active';
      case 'inactive': case 'false':                return 'status-inactive';
      case 'pending':                               return 'status-pending';
      case 'rejected':                              return 'status-rejected';
      case 'admin':                                 return 'role-admin';
      case 'minister':                              return 'role-minister';
      case 'osd':                                   return 'role-osd';
      case 'operator':                              return 'role-operator';
      case 'approver':                              return 'role-approver';
      default:                                      return 'status-pending';
    }
  }

  get statusIcon(): string {
    switch (this.normalized) {
      case 'active': case 'approved':         return 'fa-solid fa-circle-check';
      case 'inactive':                        return 'fa-solid fa-circle-minus';
      case 'pending':                         return 'fa-solid fa-clock-rotate-left';
      case 'rejected':                        return 'fa-solid fa-circle-xmark';
      case 'admin': case 'osd': case 'minister': return 'fa-solid fa-shield-halved';
      case 'operator':                        return 'fa-solid fa-user-gear';
      case 'approver':                        return 'fa-solid fa-user-check';
      default:                                return 'fa-solid fa-circle-info';
    }
  }
}
